import { t } from "i18next";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useCallback, useEffect, useMemo, useState } from "react";
import { emptyTeam, Team } from "../../../../interfaces/team";
import { getTeamSingle } from "../../../../services/team";
import { getSeasonOptions, getSeasonCurrent } from "../../../../services/season";
import { Season } from "../../../../interfaces/season";
import { createAthleteTeamSeason, deleteAthleteTeamSeason, getAthleteOptions, getTeamSeasonAthletes } from "../../../../services/athlete";
import { Athlete, AthleteTeamSeason } from "../../../../interfaces/athlete";
import DataTable from "react-data-table-component";
import { LoadingSpinner } from "../../../general/loading-spinner";
import { tableStyles, getPaginationOptions } from "../../../../util/data-table";

interface TeamAthletesProps {
    id: number;
    onSave?: CallableFunction;
    onClose: CallableFunction;
}

export function TeamAthletes(props: TeamAthletesProps) {
    const [loaded, setLoaded] = useState(false);
    const [seasons, setSeasons] = useState<Season[]>([]);
    const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
    const [athletes, setAthletes] = useState<Athlete[]>([]);
    const [team, setTeam] = useState<Team>(emptyTeam);
    const [athletesTeamSeason, setAthletesTeamSeason] = useState<AthleteTeamSeason[]>([]);

    // 'list' = view assigned athletes, 'add' = assign new athletes
    const [mode, setMode] = useState<'list' | 'add'>('list');

    // Assigned athletes (list mode)
    const [searchAssigned, setSearchAssigned] = useState('');
    const [selectedAssigned, setSelectedAssigned] = useState<AthleteTeamSeason[]>([]);
    const [toggleClearAssigned, setToggleClearAssigned] = useState(false);

    // Available athletes (add mode)
    const [searchAvailable, setSearchAvailable] = useState('');
    const [selectedAvailable, setSelectedAvailable] = useState<Athlete[]>([]);
    const [toggleClearAvailable, setToggleClearAvailable] = useState(false);

    // In-progress feedback for bulk add/remove
    const [processing, setProcessing] = useState(false);
    const [progressDone, setProgressDone] = useState(0);
    const [progressTotal, setProgressTotal] = useState(0);

    useEffect(() => {
        Promise.all([
            getSeasonOptions(),
            getTeamSingle(props.id),
            getSeasonCurrent()
        ]).then((response: any) => {
            setSeasons(response[0].data);
            const loadedTeam = response[1].data;
            setTeam(loadedTeam);

            const currentSeasonId = response[2].data && response[2].data.id
                ? parseInt(response[2].data.id) : 0;
            setSelectedSeasonId(currentSeasonId);

            getAthleteOptions(loadedTeam.club_id ? loadedTeam.club_id.toString() : '')
                .then((responseAthletes: any) => {
                    setAthletes(dedupeAthletes(responseAthletes.data));

                    if (currentSeasonId > 0) {
                        getTeamSeasonAthletes(loadedTeam.id, currentSeasonId)
                            .then((responseAts: any) => {
                                setAthletesTeamSeason(responseAts.data);
                                setLoaded(true);
                            });
                    } else {
                        setLoaded(true);
                    }
                });
        });
    }, []);

    const dedupeAthletes = (list: Athlete[]) => {
        const seen = new Set<number>();
        return list.filter((a: Athlete) => {
            if (seen.has(a.id)) {
                return false;
            }
            seen.add(a.id);
            return true;
        });
    }

    const reloadTeamSeasonAthletes = () => {
        if (selectedSeasonId > 0) {
            return getTeamSeasonAthletes(team.id, selectedSeasonId)
                .then((response: any) => {
                    setAthletesTeamSeason(response.data);
                });
        }
        return Promise.resolve();
    }

    const handleClose = () => {
        props.onClose();
    }

    const resetSelections = () => {
        setSelectedAssigned([]);
        setSelectedAvailable([]);
        setToggleClearAssigned(prev => !prev);
        setToggleClearAvailable(prev => !prev);
    }

    const handleSeasonChange = (event: any) => {
        const seasonId = event.target.value ? parseInt(event.target.value) : 0;
        setSelectedSeasonId(seasonId);
        setMode('list');
        setSearchAssigned('');
        setSearchAvailable('');
        resetSelections();
        if (seasonId > 0) {
            getTeamSeasonAthletes(team.id, seasonId)
                .then((response: any) => {
                    setAthletesTeamSeason(response.data);
                });
        } else {
            setAthletesTeamSeason([]);
        }
    }

    // ----- Assigned athletes (list mode) -----

    const filteredAssigned = useMemo(() => {
        const term = searchAssigned.trim().toLowerCase();
        if (!term) {
            return athletesTeamSeason;
        }
        return athletesTeamSeason.filter((ats: AthleteTeamSeason) =>
            (ats.athlete_name || '').toLowerCase().includes(term));
    }, [athletesTeamSeason, searchAssigned]);

    const handleSelectedAssignedChange = useCallback((state: any) => {
        setSelectedAssigned(state.selectedRows);
    }, []);

    const handleRemoveSelected = () => {
        if (selectedAssigned.length === 0) {
            return;
        }
        if (!window.confirm(t('delete_ats_message'))) {
            return;
        }

        setProgressDone(0);
        setProgressTotal(selectedAssigned.length);
        setProcessing(true);
        Promise.all(
            selectedAssigned.map((ats: AthleteTeamSeason) =>
                deleteAthleteTeamSeason(ats.id)
                    .then((response: any) => ({ ats, deleted: response.data != 0 }))
                    .catch(() => ({ ats, deleted: false }))
                    .finally(() => setProgressDone(prev => prev + 1))
            )
        ).then((results: any[]) => {
            const blocked = results.filter(r => !r.deleted);
            reloadTeamSeasonAthletes().then(() => {
                resetSelections();
                setProcessing(false);
                if (blocked.length > 0) {
                    const names = blocked.map(r => r.ats.athlete_name).join(', ');
                    alert(t("Athlete team season cannot be deleted. There is data related.") + ' ' + names);
                }
            });
        });
    }

    // ----- Available athletes (add mode) -----

    const availableAthletes = useMemo(() => {
        return athletes.filter((a: Athlete) =>
            athletesTeamSeason.findIndex((ats: AthleteTeamSeason) => ats.athlete_id == a.id) == -1);
    }, [athletes, athletesTeamSeason]);

    const filteredAvailable = useMemo(() => {
        const term = searchAvailable.trim().toLowerCase();
        if (!term) {
            return availableAthletes;
        }
        return availableAthletes.filter((a: Athlete) =>
            (a.name || '').toLowerCase().includes(term));
    }, [availableAthletes, searchAvailable]);

    const handleSelectedAvailableChange = useCallback((state: any) => {
        setSelectedAvailable(state.selectedRows);
    }, []);

    const handleEnterAddMode = (event: any) => {
        event.preventDefault();
        setSearchAvailable('');
        resetSelections();
        setMode('add');
    }

    const handleExitAddMode = (event: any) => {
        event.preventDefault();
        resetSelections();
        setMode('list');
    }

    const handleAssignSelected = () => {
        if (selectedAvailable.length === 0) {
            return;
        }

        setProgressDone(0);
        setProgressTotal(selectedAvailable.length);
        setProcessing(true);
        Promise.all(
            selectedAvailable.map((a: Athlete) =>
                createAthleteTeamSeason({
                    athlete_id: a.id,
                    team_id: team.id,
                    season_id: selectedSeasonId
                })
                    .then((response: any) => ({ athlete: a, ok: response.data.status == 1 }))
                    .catch(() => ({ athlete: a, ok: false }))
                    .finally(() => setProgressDone(prev => prev + 1))
            )
        ).then((results: any[]) => {
            const failed = results.filter(r => !r.ok);
            reloadTeamSeasonAthletes().then(() => {
                resetSelections();
                setSearchAvailable('');
                setMode('list');
                setProcessing(false);
                if (failed.length > 0) {
                    const names = failed.map(r => r.athlete.name).join(', ');
                    alert(t("Error al asignar el deportista al equipo y temporada") + ': ' + names);
                }
            });
        });
    }

    const assignedColumns = [
        {
            id: 'athlete_name',
            name: t('athlete'),
            selector: (row: any) => row.athlete_name,
            sortable: true
        },
        {
            id: 'dorsal',
            name: t('dorsal'),
            selector: (row: any) => row.dorsal,
            sortable: true
        },
        {
            id: 'position',
            name: t('position'),
            selector: (row: any) => row.position,
            sortable: true
        }
    ];

    const availableColumns = [
        {
            id: 'name',
            name: t('athlete'),
            selector: (row: any) => row.name,
            sortable: true
        }
    ];

    return (
        <Modal className="athlete-team-seasons-container team-athletes-modal" show={ true }
            onHide={ handleClose } size="xl">
            <Modal.Header closeButton>
                <Modal.Title>
                    { t("team_athletes") } { team.name ? '- ' + team.name : '' }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                { !loaded &&
                    <LoadingSpinner />
                }
                { loaded &&
                    <div className="team-athletes-container">
                        { selectedSeasonId > 0 && mode === 'list' &&
                            <div className="assigned-athletes">
                                <div className="toolbar filters d-flex flex-wrap gap-2 align-items-end mb-3">
                                    <Form.Group className="filter-season" controlId="season_id">
                                        <Form.Label aria-required="true">{ t("season") }</Form.Label>
                                        <Form.Select aria-label={ t("season") } name="season_id" required
                                            value={ selectedSeasonId }
                                            onChange={ handleSeasonChange }>
                                            <option value="">{ t("select_season") }</option>
                                            { seasons.map((s: Season) => {
                                                return <option key={ 'season-' + s.id } value={ s.id }>{ s.name }</option>;
                                            })}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="filter-search">
                                        <Form.Control type="search" className="search-field"
                                            placeholder={ t("Write to search") }
                                            value={ searchAssigned }
                                            onChange={ (e) => setSearchAssigned(e.target.value) } />
                                    </Form.Group>
                                    <div className="actions ms-auto d-flex gap-2 align-items-center">
                                        { processing &&
                                            <span className="processing-indicator d-inline-flex align-items-center">
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                { t("Removing... ({{done}} of {{total}})", { done: progressDone, total: progressTotal }) }
                                            </span>
                                        }
                                        { selectedAssigned.length > 0 &&
                                            <Button variant="danger" onClick={ handleRemoveSelected } disabled={ processing }>
                                                <i className="bi bi-trash me-1"></i>
                                                { t("Remove from team") } ({ selectedAssigned.length })
                                            </Button>
                                        }
                                        <Button variant="primary" onClick={ handleEnterAddMode } disabled={ processing }>
                                            <i className="bi bi-plus-circle me-1"></i>
                                            { t("add") }
                                        </Button>
                                    </div>
                                </div>
                                <div className="subtable-container">
                                    <DataTable
                                        columns={ assignedColumns }
                                        data={ filteredAssigned }
                                        selectableRows={ true }
                                        selectableRowsHighlight={ true }
                                        onSelectedRowsChange={ handleSelectedAssignedChange }
                                        clearSelectedRows={ toggleClearAssigned }
                                        responsive={ true }
                                        striped={ true }
                                        highlightOnHover={ true }
                                        defaultSortFieldId={ 'athlete_name' }
                                        defaultSortAsc={ true }
                                        pagination={ true }
                                        customStyles={ tableStyles }
                                        paginationComponentOptions={ getPaginationOptions() }
                                    />
                                </div>
                            </div>
                        }

                        { selectedSeasonId > 0 && mode === 'add' &&
                            <div className="add-athletes-layer">
                                <div className="toolbar filters d-flex flex-wrap gap-2 align-items-end mb-3">
                                    <Form.Group className="filter-season" controlId="season_id_add">
                                        <Form.Label aria-required="true">{ t("season") }</Form.Label>
                                        <Form.Select aria-label={ t("season") } name="season_id_add" required
                                            value={ selectedSeasonId }
                                            onChange={ handleSeasonChange }>
                                            <option value="">{ t("select_season") }</option>
                                            { seasons.map((s: Season) => {
                                                return <option key={ 'season-add-' + s.id } value={ s.id }>{ s.name }</option>;
                                            })}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="filter-search">
                                        <Form.Control type="search" className="search-field"
                                            placeholder={ t("Write to search") }
                                            value={ searchAvailable }
                                            onChange={ (e) => setSearchAvailable(e.target.value) } />
                                    </Form.Group>
                                    <div className="actions ms-auto d-flex gap-2 align-items-center">
                                        { processing &&
                                            <span className="processing-indicator d-inline-flex align-items-center">
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                { t("Adding... ({{done}} of {{total}})", { done: progressDone, total: progressTotal }) }
                                            </span>
                                        }
                                        { selectedAvailable.length > 0 &&
                                            <Button variant="primary" onClick={ handleAssignSelected } disabled={ processing }>
                                                <i className="bi bi-check2-circle me-1"></i>
                                                { t("Add to team") } ({ selectedAvailable.length })
                                            </Button>
                                        }
                                        <Button variant="secondary" onClick={ handleExitAddMode } disabled={ processing }>
                                            { t("Back") }
                                        </Button>
                                    </div>
                                </div>
                                <div className="subtable-container">
                                    <DataTable
                                        columns={ availableColumns }
                                        data={ filteredAvailable }
                                        selectableRows={ true }
                                        selectableRowsHighlight={ true }
                                        onSelectedRowsChange={ handleSelectedAvailableChange }
                                        clearSelectedRows={ toggleClearAvailable }
                                        responsive={ true }
                                        striped={ true }
                                        highlightOnHover={ true }
                                        defaultSortFieldId={ 'name' }
                                        defaultSortAsc={ true }
                                        pagination={ true }
                                        customStyles={ tableStyles }
                                        paginationComponentOptions={ getPaginationOptions() }
                                        noDataComponent={ <div className="p-3">{ t("No athletes available") }</div> }
                                    />
                                </div>
                            </div>
                        }
                    </div>
                }
            </Modal.Body>
        </Modal>
    );
}
